import type { IStyleParam, IStyleParamValuesByType, StyleParamType, createStylesParams } from '@wix/tpa-settings';
import type { IDisplayStylesParams } from './Settings/Tabs/DisplayTab/DisplayStylesParams';
import { DisplayStylesParams } from './Settings/Tabs/DisplayTab/DisplayStylesParams';
import type { IDesignStylesParams } from './Settings/Tabs/DesignTab/DesignStylesParams';
import { DesignStylesParams } from './Settings/Tabs/DesignTab/DesignStylesParams';
import type { ILayoutStylesParams } from './Settings/Tabs/LayoutTab/LayoutStylesParams';
import { LayoutStylesParams } from './Settings/Tabs/LayoutTab/LayoutStylesParams';
import type { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import {
  getColumnsImageWidth,
  getItemTextAlignment,
  getMenuMargin,
  getMenuMaxWidth,
  getMenuTextAlignment,
  getNavigationMargin,
  getNavigationTextAlignment,
  getSectionsColumns,
  getSectionsColumnsGap,
  getSectionsDisplay,
  getSectionsGridTemplateColumns,
  getSectionsMarginTop,
  getSectionsRowGap,
  getSectionTextAlignment,
  getWidgetGridTemplate,
  showMenuSideImage,
  showPriceVariantsDivider,
  getElementDisplay,
  getSectionPadding,
  getSectionMarginBottom,
  getSectionFirstDividerMarginTop,
  getSectionTopDividerMarginBottom,
  getItemsDisplay,
  getItemsGridTemplateColumns,
  getItemsRowGap,
  getItemsColumnGap,
  getItemRootMarginBottom,
  getItemGridTemplate,
  getItemGridTemplateNoImage,
  getItemPadding,
  getItemPriceJustifySelf,
  getItemPriceMarginBottom,
  getItemImageAspectRatio,
  getItemImageMarginBottom,
  getPriceVariantsGridArea,
  getPriceVariantsDisplay,
  getPriceVariantsJustifyContent,
  getPriceVariantsAlignItems,
  getPriceVariantsFlexWrap,
  getPriceVariantsGap,
  getPriceVariantsMarginBottom,
  getVariantDisplay,
  getVariantGridTemplate,
  getVariantAlignItems,
  getVariantWidth,
  getVariantDetailsJustifyContent,
  getVariantDetailsGridArea,
  getWidgetPadding,
  getWidgetOverflow,
  getWidgetDisplay,
  getNavigationContainerGridArea,
  getNavigationContainerPadding,
  getMenuContentGridArea,
  getMenuContentPadding,
  getImageContainerGridArea,
  getImageContainerPosition,
  getImageContainerTop,
  getImageContainerWidth,
  getImageContainerHeight,
  getImageContainerPaddingBottom,
} from './stylesParamsUtils';
import { hasItemDivider, hasItemImage, hasSectionBottomDivider, hasSectionTopDivider } from 'root/utils/presets';

export type StylesParamsType<T extends Record<string, StyleParamType>> = ReturnType<typeof createStylesParams<T>>;

export type IStylesParams = IDesignStylesParams & IDisplayStylesParams & ILayoutStylesParams;

export const stylesParams: StylesParamsType<IStylesParams> = {
  ...DisplayStylesParams,
  ...DesignStylesParams,
  ...LayoutStylesParams,
};

export default stylesParams;

export type StyleParamValue = IStyleParamValuesByType[keyof IStyleParamValuesByType];

export type StyleParamsByType<T extends StyleParamType> = {
  [K in keyof IStylesParams as IStylesParams[K] extends T ? K : never]: IStylesParams[K];
};

export type StyleParamKeysByType<T extends StyleParamType> = keyof StyleParamsByType<T>;

export type ExtractStyleParamValue<T> = T extends IStyleParam<StyleParamType, infer U> ? U : never;

export const customCssVars: CustomCssVarsFn = ({ isMobile, isRTL, styleParams, tpaData }) => {
  const preset = styleParams.numbers.preset;

  const columnsImageWidth = getColumnsImageWidth({
    imageWidthEnum: styleParams.numbers.columnsItemImageSize,
    isMobile,
  });

  const menuTextAlignment = getMenuTextAlignment({
    gridMenuAlignment: styleParams.numbers.gridMenuAlignment,
    columnsMenuAlignment: styleParams.numbers.columnsMenuAlignment,
    sideBySideMenuAlignment: styleParams.numbers.sideBySideMenuAlignment,
    preset,
  });

  const sectionTextAlignment = getSectionTextAlignment({
    gridSectionAlignment: styleParams.numbers.gridSectionAlignment,
    columnsSectionAlignment: styleParams.numbers.columnsSectionAlignment,
    sideBySideSectionAlignment: styleParams.numbers.sideBySideSectionAlignment,
    preset,
  });

  const itemTextAlignment = getItemTextAlignment({
    gridItemAlignment: styleParams.numbers.gridItemAlignment,
    preset,
  });

  const navigationTextAlignment = getNavigationTextAlignment({
    preset,
    isRTL,
    gridMenuNavigationAlignment: styleParams.numbers.gridMenuNavigationAlignment,
    columnsMenuNavigationAlignment: styleParams.numbers.columnsMenuNavigationAlignment,
    sideBySideMenuNavigationAlignment: styleParams.numbers.sideBySideMenuNavigationAlignment,
  });

  const showVariantsDivider = showPriceVariantsDivider({
    preset,
    showItemVariantsDivider: styleParams.booleans.showItemVariantsDivider,
  });

  const showMenusSideImage = showMenuSideImage({
    preset,
    isMobile,
    showMenusSideImage: styleParams.booleans.showMenusSideImage,
  });

  const widgetGridTemplate = getWidgetGridTemplate({
    preset,
    isMobile,
    isRTL,
    imagePosition: styleParams.numbers.sideBySideImagePosition,
  });

  const widgetPadding = getWidgetPadding({
    preset,
    gridMenuVerticalPadding: styleParams.numbers.gridMenuVerticalPadding,
    gridMenuHorizontalPadding: styleParams.numbers.gridMenuHorizontalPadding,
    columnsMenuVerticalPadding: styleParams.numbers.columnsMenuVerticalPadding,
    columnsMenuHorizontalPadding: styleParams.numbers.columnsMenuHorizontalPadding,
    centerMenuVerticalPadding: styleParams.numbers.centerMenuVerticalPadding,
    centerMenuHorizontalPadding: styleParams.numbers.centerMenuHorizontalPadding,
    sideBySideMenuVerticalPadding: styleParams.numbers.sideBySideMenuVerticalPadding,
    sideBySideMenuHorizontalPadding: styleParams.numbers.sideBySideMenuHorizontalPadding,
  });

  const widgetOverflow = getWidgetOverflow({ preset, isMobile });
  const widgetDisplay = getWidgetDisplay({ preset });
  const navigationContainerGridArea = getNavigationContainerGridArea({ preset });
  const navigationContainerPadding = getNavigationContainerPadding({ preset, isMobile });
  const menuContentGridArea = getMenuContentGridArea({ preset });
  const menuContentPadding = getMenuContentPadding({ preset, isMobile });
  const imageContainerGridArea = getImageContainerGridArea({ preset });
  const imageContainerPosition = getImageContainerPosition({ preset });
  const imageContainerTop = getImageContainerTop({ preset });
  const imageContainerWidth = getImageContainerWidth({ preset });
  const imageContainerHeight = getImageContainerHeight({ preset, isMobile });
  const imageContainerPaddingBottom = getImageContainerPaddingBottom({ preset, isMobile });

  const navigationMargin = getNavigationMargin({ isMobile });
  const menuMaxWidth = getMenuMaxWidth({ preset });
  const menuMargin = getMenuMargin({ preset });
  const sectionsDisplay = getSectionsDisplay({ preset });
  const sectionsGridTemplateColumns = getSectionsGridTemplateColumns({ preset });
  const sectionsRowGap = getSectionsRowGap({
    preset,
    gridGap: styleParams.numbers.gridSectionsGap,
    centerGap: styleParams.numbers.centerSectionsGap,
  });
  const sectionsMarginTop = getSectionsMarginTop({ preset });
  const sectionsColumns = getSectionsColumns({
    preset,
    isMobile,
    columnsNumOfColumns: styleParams.numbers.columnsNumOfColumns,
  });
  const sectionsColumnsGap = getSectionsColumnsGap({
    preset,
    columnsGap: styleParams.numbers.columnsColumnsGap,
  });
  const sectionPadding = getSectionPadding({
    preset,
    gridPadding: styleParams.numbers.gridSectionPadding,
    columnsPadding: styleParams.numbers.columnsSectionPadding,
    centerPadding: styleParams.numbers.centerSectionPadding,
    sideBySidePadding: styleParams.numbers.sideBySideSectionPadding,
  });
  const sectionMarginBottom = getSectionMarginBottom({
    preset,
    columnsMarginBottom: styleParams.numbers.columnsSectionsGap,
    sideBySideMarginBottom: styleParams.numbers.sideBySideSectionsGap,
  });
  const sectionFirstDividerMarginTop = getSectionFirstDividerMarginTop({ preset });
  const sectionTopDividerMarginBottom = getSectionTopDividerMarginBottom({
    preset,
    centerSectionDividerMargin: styleParams.numbers.centerSectionDividerMargin,
  });
  const itemsDisplay = getItemsDisplay({ preset });
  const itemsGridTemplateColumns = getItemsGridTemplateColumns({
    preset,
    isMobile,
    gridNumOfColumns: styleParams.numbers.gridNumOfColumns,
  });

  const itemsColumnGap = getItemsColumnGap({ preset, gridItemsColumnGap: styleParams.numbers.gridItemsColumnGap });
  const itemsRowGap = getItemsRowGap({
    preset,
    gridItemsRowGap: styleParams.numbers.gridItemsRowGap,
    centerItemsRowGap: styleParams.numbers.centerItemsRowGap,
  });

  const itemRootMarginBottom = getItemRootMarginBottom({
    preset,
    columnsItemsGap: styleParams.numbers.columnsItemsGap,
    sideBySideItemsGap: styleParams.numbers.sideBySideItemsGap,
  });
  const itemGridTemplate = getItemGridTemplate({
    preset,
    isRTL,
    gridItemDetailsPadding: styleParams.numbers.gridItemDetailsPadding,
    columnsImageWidth,
    centerItemDetailsPadding: styleParams.numbers.centerItemDetailsPadding,
    columnsImagePosition: styleParams.numbers.columnsImagePosition,
    showItemImage: styleParams.booleans.showItemImage,
  });
  const itemGridTemplateNoImage = getItemGridTemplateNoImage({ preset });
  const itemPadding = getItemPadding({
    preset,
    gridItemPadding: styleParams.numbers.gridItemPadding,
    columnsItemPadding: styleParams.numbers.columnsItemPadding,
    sideBySideItemPadding: styleParams.numbers.sideBySideItemPadding,
    centerItemPadding: styleParams.numbers.centerItemPadding,
  });
  const itemPriceJustifySelf = getItemPriceJustifySelf({ preset });
  const itemPriceMarginBottom = getItemPriceMarginBottom({ preset });
  const itemImageAspectRatio = getItemImageAspectRatio({
    preset,
    gridItemImageRatio: styleParams.numbers.gridItemImageRatio,
    columnsItemImageRatio: styleParams.numbers.columnsItemImageRatio,
  });
  const itemImageMarginBottom = getItemImageMarginBottom({
    preset,
    gridItemImageBottomPadding: styleParams.numbers.gridItemImageBottomPadding,
  });

  const priceVariantsGridArea = getPriceVariantsGridArea({ preset });
  const priceVariantsDisplay = getPriceVariantsDisplay({ preset });
  const priceVariantsJustifyContent = getPriceVariantsJustifyContent({ preset });
  const priceVariantsAlignItems = getPriceVariantsAlignItems({ preset });
  const priceVariantsFlexWrap = getPriceVariantsFlexWrap({ preset });
  const priceVariantsGap = getPriceVariantsGap({ preset });
  const priceVariantsMarginBottom = getPriceVariantsMarginBottom({ preset });

  const variantDisplay = getVariantDisplay({ preset });
  const variantGridTemplate = getVariantGridTemplate({ preset });
  const variantAlignItems = getVariantAlignItems({ preset });
  const variantWidth = getVariantWidth({ preset });
  const variantDetailsJustifyContent = getVariantDetailsJustifyContent({ preset });
  const variantDetailsGridArea = getVariantDetailsGridArea({ preset });

  const showItemDescription = getElementDisplay({ shouldShow: styleParams.booleans.showItemDescription });

  const showItemPrice = getElementDisplay({ shouldShow: styleParams.booleans.showItemPrice });

  const showItemCurrency = getElementDisplay({ shouldShow: styleParams.booleans.showItemCurrency });

  const showItemLabels = getElementDisplay({
    shouldShow:
      styleParams.booleans.showItemLabels &&
      (styleParams.booleans.showItemLabelsIcon || styleParams.booleans.showItemLabelsName),
    displayOption: 'flex',
  });

  const showItemLabelsIcon = getElementDisplay({ shouldShow: styleParams.booleans.showItemLabelsIcon });

  const showItemLabelsName = getElementDisplay({ shouldShow: styleParams.booleans.showItemLabelsName });

  const showItemImage = getElementDisplay({ shouldShow: styleParams.booleans.showItemImage && hasItemImage(preset) });

  const showItemDividers = getElementDisplay({
    shouldShow: styleParams.booleans.showItemDividers && hasItemDivider(preset),
  });

  const showItemVariantsPrice = getElementDisplay({ shouldShow: styleParams.booleans.showItemVariantsPrice });

  const showItemVariantsName = getElementDisplay({ shouldShow: styleParams.booleans.showItemVariantsName });

  const showTabs = getElementDisplay({ shouldShow: styleParams.booleans.showTabs });

  const showMenuName = getElementDisplay({ shouldShow: styleParams.booleans.showMenuName });

  const showMenuDescription = getElementDisplay({ shouldShow: styleParams.booleans.showMenuDescription });

  const showSectionName = getElementDisplay({ shouldShow: styleParams.booleans.showSectionName });

  const showSectionDescription = getElementDisplay({ shouldShow: styleParams.booleans.showSectionDescription });

  const showSectionTopDivider = getElementDisplay({
    shouldShow: styleParams.booleans.showSectionDividers && hasSectionTopDivider(preset),
  });

  const showSectionBottomDivider = getElementDisplay({
    shouldShow: styleParams.booleans.showSectionDividers && hasSectionBottomDivider(preset),
  });

  const customCss = {
    columnsImageWidth,
    menuTextAlignment,
    sectionTextAlignment,
    itemTextAlignment,
    navigationTextAlignment,
    showVariantsDivider,
    showMenusSideImage,
    widgetPadding,
    widgetOverflow,
    widgetDisplay,
    widgetGridTemplate,
    navigationContainerGridArea,
    navigationContainerPadding,
    menuContentGridArea,
    menuContentPadding,
    imageContainerGridArea,
    imageContainerPosition,
    imageContainerWidth,
    imageContainerHeight,
    imageContainerPaddingBottom,
    imageContainerTop,
    navigationMargin,
    menuMaxWidth,
    menuMargin,
    sectionsDisplay,
    sectionsGridTemplateColumns,
    sectionsRowGap,
    sectionsMarginTop,
    sectionsColumns,
    sectionsColumnsGap,
    sectionPadding,
    sectionMarginBottom,
    sectionFirstDividerMarginTop,
    sectionTopDividerMarginBottom,
    itemsDisplay,
    itemsGridTemplateColumns,
    itemsColumnGap,
    itemsRowGap,
    itemRootMarginBottom,
    itemGridTemplate,
    itemGridTemplateNoImage,
    itemPadding,
    itemPriceJustifySelf,
    itemPriceMarginBottom,
    itemImageAspectRatio,
    itemImageMarginBottom,
    priceVariantsGridArea,
    priceVariantsDisplay,
    priceVariantsJustifyContent,
    priceVariantsAlignItems,
    priceVariantsFlexWrap,
    priceVariantsGap,
    priceVariantsMarginBottom,
    variantDisplay,
    variantGridTemplate,
    variantAlignItems,
    variantWidth,
    variantDetailsJustifyContent,
    variantDetailsGridArea,
    showItemDescription,
    showItemPrice,
    showItemCurrency,
    showItemLabels,
    showItemImage,
    showItemDividers,
    showItemLabelsIcon,
    showItemLabelsName,
    showItemVariantsPrice,
    showItemVariantsName,
    showTabs,
    showMenuName,
    showMenuDescription,
    showSectionName,
    showSectionDescription,
    showSectionTopDivider,
    showSectionBottomDivider,
  };

  return Object.fromEntries(Object.entries(customCss).filter(([_, value]) => value !== ''));
};
